import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Clock, CheckCircle, XCircle, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MySupportTicketsPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentLang = i18n.language || 'es';

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        toast({
          title: t('supportTickets.error.authRequired'),
          description: t('supportTickets.error.authDescription'),
          variant: 'destructive'
        });
        return;
      }

      const response = await axios.get(`${API}/support/my-tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Load tickets error:', error);
      toast({
        title: t('common.error'),
        description: t('supportTickets.error.load'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      open: Clock,
      in_progress: MessageSquare,
      resolved: CheckCircle,
      closed: XCircle,
    };
    return icons[status] || Clock;
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'text-blue-600 bg-blue-50',
      in_progress: 'text-yellow-600 bg-yellow-50',
      resolved: 'text-green-600 bg-green-50',
      closed: 'text-gray-600 bg-gray-50',
    };
    return colors[status] || 'text-gray-600 bg-gray-50';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(currentLang.startsWith('es') ? 'es-CO' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="text-center py-12 text-gray-500">{t('supportTickets.loading')}</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('supportTickets.title')}</h2>
          <p className="text-gray-600 mt-1">{t('supportTickets.manage')}</p>
        </div>
        <Button
          onClick={() => navigate('/support/create')}
          className="bg-[#be1f1a] hover:bg-[#a71b17]"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('supportTickets.newTicket')}
        </Button>
      </div>

      {tickets.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('supportTickets.noTickets')}</h3>
          <p className="text-gray-600 mb-4">
            {t('supportTickets.noTicketsDescription')}
          </p>
          <Button
            onClick={() => navigate('/support/create')}
            className="bg-[#be1f1a] hover:bg-[#a71b17]"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('supportTickets.createTicket')}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {tickets.map((ticket) => {
            const StatusIcon = getStatusIcon(ticket.status);
            
            return (
              <Card 
                key={ticket.id} 
                className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate(`/support/ticket/${ticket.id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-mono text-gray-500">
                          #{ticket.id.substring(0, 8)}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${getStatusColor(ticket.status)}`}>
                          <StatusIcon className="w-3 h-3" />
                          {t(`supportTickets.status.${ticket.status}`)}
                        </span>
                      </div>
                      <h3 className="font-semibold text-lg text-gray-900 mb-1">
                        {ticket.subject}
                      </h3>
                      {ticket.orderNumber && (
                        <p className="text-sm text-gray-600">
                          {t('supportTickets.order')}: <span className="font-mono">{ticket.orderNumber}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  <p className="text-gray-700 line-clamp-2 mb-3">
                    {ticket.message}
                  </p>

                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>{t('supportTickets.category')}: {t(`accountMore.ticketCategory.${ticket.category}`, { defaultValue: ticket.category })}</span>
                    <span>{t('supportTickets.created')}: {formatDate(ticket.createdAt)}</span>
                  </div>

                  {ticket.lastReplyAt && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <span className="text-sm text-gray-600">
                        {t('supportTickets.lastReply')}: {formatDate(ticket.lastReplyAt)}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MySupportTicketsPage;
